import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import authService from '../services/authService';
import CustomAlert from '../components/CustomAlert';
import useCustomAlert from '../hooks/useCustomAlert';
import './LoginPage.css';

const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || 'YOUR_FACEBOOK_APP_ID';

export default function LoginPage() {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { alertProps, showSuccess } = useCustomAlert();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirecionar se já estiver logado
  useEffect(() => {
    if (authService.isAuthenticated()) {
      navigate('/meus-agendamentos');
    }
  }, [navigate]);

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      setError('');
      
      try {
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        
        const userInfo = await userInfoResponse.json();
        
        await authService.socialLogin(
          'google',
          tokenResponse.access_token,
          userInfo.email,
          userInfo.name,
          userInfo.sub
        );
        
        // Disparar evento customizado
        window.dispatchEvent(new Event('authStateChanged'));
        
        showSuccess(`Bem-vindo(a), ${userInfo.name}!`);
        setTimeout(() => navigate('/meus-agendamentos'), 1000);
      } catch (err) {
        console.error('Erro no login Google:', err);
        setError('Erro ao fazer login com Google. Tente novamente.');
      } finally {
        setLoading(false);
      }
    },
    onError: (error) => {
      console.error('Login Failed:', error);
      setError('Erro ao conectar com Google');
    },
  });

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService.login(email, password);
      
      window.dispatchEvent(new Event('authStateChanged'));
      
      setTimeout(() => navigate('/meus-agendamentos'), 1000);
    } catch (err) {
      setError(err.message || 'Erro ao fazer login. Verifique suas credenciais.');
    } finally {
      setLoading(false);
    }
  };

  const handleFacebookCallback = async (response) => {
    if (response.accessToken) {
      setLoading(true);
      setError('');
      
      try {
        await authService.socialLogin(
          'facebook',
          response.accessToken,
          response.email,
          response.name,
          response.userID
        );
        
        window.dispatchEvent(new Event('authStateChanged'));
        
        showSuccess(`Bem-vindo(a), ${response.name}!`);
        setTimeout(() => navigate('/meus-agendamentos'), 1000);
      } catch (err) {
        console.error('Erro no login Facebook:', err);
        setError('Erro ao fazer login com Facebook. Tente novamente.');
      } finally {
        setLoading(false);
      }
    } else {
      setError('Erro ao conectar com Facebook');
    }
  };

  if (showEmailForm) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <h1 className="login-title">Entrar na Conta</h1>
              <p className="login-subtitle">
                Novo no site? <a href="/signup" className="signup-link">Criar Conta</a>
              </p>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <form className="login-form" onSubmit={handleEmailLogin}>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  disabled={loading}
                  autoComplete="email"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite sua senha"
                  required
                  disabled={loading}
                  autoComplete="current-password"
                  minLength={6}
                />
              </div>

              <button 
                type="submit" 
                className="submit-btn"
                disabled={loading}
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </button>

              <div className="social-divider">
                <span>ou entre com</span>
              </div>

              <div className="social-buttons-inline">
                <button 
                  type="button" 
                  className="social-btn google-btn-inline"
                  onClick={() => googleLogin()}
                  disabled={loading}
                  title="Login com Google"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </button>

                <FacebookLogin
                  appId={FACEBOOK_APP_ID}
                  autoLoad={false}
                  fields="name,email,picture"
                  callback={handleFacebookCallback}
                  render={renderProps => (
                    <button 
                      type="button" 
                      className="social-btn facebook-btn-inline"
                      onClick={renderProps.onClick}
                      disabled={loading}
                      title="Login com Facebook"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Facebook
                    </button>
                  )}
                />
              </div>

              <button
                type="button"
                className="back-btn"
                onClick={() => setShowEmailForm(false)}
                disabled={loading}
              >
                Voltar
              </button>
            </form>
          </div>
        </div>
        <CustomAlert {...alertProps} />
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1 className="login-title">Bem-vindo de volta!</h1>
            <p className="login-subtitle">
              Novo no site? <a href="/signup" className="signup-link">Criar Conta</a>
            </p>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="social-buttons">
            <button 
              className="social-btn google-btn" 
              onClick={() => googleLogin()}
              disabled={loading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {loading ? 'Conectando...' : 'Entrar com Google'}
            </button>

            <FacebookLogin
              appId={FACEBOOK_APP_ID}
              autoLoad={false}
              fields="name,email,picture"
              callback={handleFacebookCallback}
              render={renderProps => (
                <button 
                  className="social-btn facebook-btn" 
                  onClick={renderProps.onClick}
                  disabled={loading}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  {loading ? 'Conectando...' : 'Entrar com Facebook'}
                </button>
              )}
            />
          </div>

          <div className="divider">
            <span>ou</span>
          </div>

          <button 
            className="email-btn" 
            onClick={() => setShowEmailForm(true)}
            disabled={loading}
          >
            Entrar com email
          </button>

          <div className="login-footer">
            <a href="/" className="footer-link">Voltar à página inicial</a>
          </div>
        </div>
      </div>
      <CustomAlert {...alertProps} />
    </div>
  );
}